import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import CustomButton from "../common/CustomButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Flag from "../assets/images/flag.png";
import Search from "../assets/images/search.png";
import CustomTextFeild from "../common/CustomTextFeild";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { _apiUrls } from "../utils/endpoints/apiUrls";
import { apiRequest } from "../utils/config/apiRequest";
import { useNavigate } from "react-router-dom";
import CustomModal from "../common/CustomModal";
import Pagination from "../common/Pagination";
import CustomSelect from "../common/CustomSelect";
import CustomTooltip from "../common/CustomTooltip";
import WhiteSearch from "../assets/images/whitesearch.png";
import { getTheme } from "../redux/selectors/appSelectors";
import { useSelector } from "react-redux";

export default function RolesManagement() {
  const theme = useSelector(getTheme);
  const isDarkMode = theme === "dark";

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [state, setState] = useState({ rows: [] });
  const [searchText, setSearchText] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 5;

  // filter states ------------------------------

  const [filterOptions, setFilterOptions] = useState({
    memberId: [],
    email: [],
    country: [],
    status: [],
  });

  const [filters, setFilters] = useState({
    memberId: "",
    email: "",
    country: "",
    status: "",
  });
  const [filtersCleared, setFiltersCleared] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);

    const { data, status } = await apiRequest({
      endUrl: _apiUrls.getAllAdmins,
      method: "GET",
      query: {
        search: searchText,
        page: currentPage,
        limit: itemsPerPage,
        ...filters,
      },
    });
    if (status) {
      setState({ ...state, rows: Array.isArray(data?.rows) ? data.rows : [] });
      setTotalItems(data?.count || 0);
    }
    setLoading(false);
  };

  // Delete Users -----------------------------------------

  const [modalShow, setModalShow] = useState(false);
  const [modalState, setModalState] = useState({ name: "", user: null });

  const handleModalAction = (type, user) => {
    setModalState({ name: type, user });
    setModalShow(true);
  };

  const confirmDelete = async () => {
    const user = modalState?.user;
    if (!user) return;
    const { status } = await apiRequest({
      endUrl: _apiUrls.deleteUser,
      method: "DELETE",
      query: { userId: user._id },
      showMsg: true,
    });
    if (status) {
      fetchUsers();
    }
    setModalShow(false);
    setModalState({ name: "", user: null });
  };

  // filters ----------------------------------------------

  const fetchFilterOptions = async () => {
    const types = ["memberId", "email", "country", "status"];
    const results = {};

    for (const type of types) {
      const { data, status } = await apiRequest({
        endUrl: _apiUrls.filterUser,
        method: "GET",
        query: { filterType: type },
      });

      if (status && Array.isArray(data?.rows)) {
        results[type] = data.rows.map((val) => ({
          label: val?.toString(),
          value: val?.toString(),
        }));
      } else {
        results[type] = [];
      }
    }

    setFilterOptions(results);
  };

  const clearFilters = () => {
    setFilters({
      memberId: "",
      email: "",
      country: "",
      status: "",
    });
    setFiltersCleared(true); // ✅ mark that filters were cleared
  };

  useEffect(() => {
    fetchUsers();
  }, [searchText, currentPage]);

  useEffect(() => {
    if (filtersCleared) {
      fetchUsers(); // ✅ run fetch after filters are cleared
      setFiltersCleared(false); // reset flag
    }
  }, [filtersCleared]);

  // Export Function -----------------------------------------------------------

  const handleExport = async () => {
    try {
      const { data, status } = await apiRequest({
        endUrl: _apiUrls.getUser,
        method: "GET",
        query: {
          search: searchText,
          ...filters,
          exportFlag: true,
        },
      });

      const fileUrl = data?.filePath;

      if (status && fileUrl) {
        window.open(fileUrl, "_blank");
      } else {
        console.error("Export failed: filePath missing or status false");
      }
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  return (
    <>
      <Row>
        <Col md={12} className="py-3">
          <h4 className="dashheading">Manage Admins</h4>
          <p className="dashsubheading">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            list
          </p>
        </Col>
      </Row>
      <Row>
        <Col md={9} xs={12} className="pb-3">
          <div className="scroll-buttons-container">
            <div className="d-flex gap-3">
              <CustomButton variant="contained"
                text={"Add More"}
                iconLeft={<PersonAddIcon style={{ fontSize: 18 }} />}
                className='containebtn'
                onClick={() => navigate('/dashboard/addAdmin')}
              />

              <CustomButton
                variant="contained"
                onClick={handleExport}
                text="Export"
                iconLeft={<UploadFileOutlinedIcon style={{ fontSize: 18 }} />}
                className="containebtn"
              />

              <CustomButton
                variant="contained"
                onClick={() => {
                  setFilterModal(true);
                  fetchFilterOptions();
                }}
                text="Filter"
                iconLeft={<FilterAltRoundedIcon style={{ fontSize: 18 }} />}
                className="containebtn"
              />

              <CustomButton
                variant="outline2"
                onClick={clearFilters}
                text="Clear Filters"
                className="outline2-btn"
              />
            </div>
          </div>
        </Col>

        <Col md={3} xs={12} className="pb-3">
          <CustomTextFeild
            style={{
              padding: " 7px 13px",
              borderRadius: "10px",
              height: "auto",
            }}
            icon={isDarkMode ? WhiteSearch : Search}
            iconHeight="15px"
            placeholder="Search Admins...."
            value={searchText}
            // onChange={(e) => setSearchText(e.target.value)}
            onChange={(e) => {
              const value = e.target.value;
              setSearchText(value);
              if (value === "") {
                setCurrentPage(1); // ✅ reset to first page on clear
              }
            }}
          />
        </Col>

        <Col md={12} className="mt-2">
          <div className="head-border" />
        </Col>

        <Col md={12} className="mt-3">
          <div className="table-responsive">
            <table className="admintable">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Admin Id</th>
                  <th>Admin Detail</th>
                  <th>Email Id</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="d-flex justify-content-center align-items-center">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : state.rows.length > 0 ? (
                  state.rows.map((item, index) => {
                    return (
                      <tr key={item?._id || index}>
                        <td>{index + 1}</td>
                        <td>{item.memberId || "----"}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {item?.profilePic ? (
                              <>
                                {(() => {
                                  let fullImageUrl = item?.profilePic;
                                  if (!item?.profilePic?.startsWith("http")) {
                                    fullImageUrl = `${process.env.REACT_APP_API_ORIGIN}/${item?.profilePic?.replace(/^\/+/, "")}`;
                                  }

                                  return (
                                    <img
                                      src={fullImageUrl}
                                      alt=""
                                      style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "50%",
                                      }}
                                    />
                                  );
                                })()}
                              </>
                            ) : (
                              <div className="user-pic">
                                <p className="mb-0">
                                  {(item?.fullName || "NA")
                                    .substring(0, 2)
                                    .toUpperCase()}
                                </p>
                              </div>
                            )}

                            <div>
                              <strong>{item?.fullName || "----"}</strong>
                              <br />
                              <small>{item?.mobile}</small>
                            </div>
                          </div>
                        </td>
                        <td>{item?.email || "----"}</td>
                        <td>{item?.country?.name || "----"}</td>
                        <td>
                          <span
                            className={`badge badge-status ${item.status === "active"
                              ? "bg-success"
                              : "badge-danger"
                              }`}
                          >
                            {item?.status?.charAt(0).toUpperCase() +
                              item?.status?.slice(1)}
                          </span>
                        </td>
                        <td>
                          <div className="gap-2 d-flex align-items-center">
                            <CustomTooltip title="Edit">
                              <p
                                className="mb-0"
                                onClick={() =>
                                  navigate(`/dashboard/addAdmin/${item._id}`)
                                }
                                style={{ textDecoration: "none" }}
                              >
                                <EditIcon
                                  sx={{
                                    color: isDarkMode
                                      ? "rgba(75, 128, 235, 1)"
                                      : "#2C2C2C",
                                    cursor: "pointer",
                                    fontSize: 18,
                                  }}
                                />
                              </p>
                            </CustomTooltip>

                            <CustomTooltip title="View">
                              <VisibilityIcon
                                onClick={() => handleModalAction("view", item)}
                                sx={{
                                  color: "rgba(75, 128, 235, 1)",
                                  cursor: "pointer",
                                  fontSize: 18,
                                }}
                              />
                            </CustomTooltip>

                            <CustomTooltip title="Delete">
                              <DeleteIcon
                                onClick={() =>
                                  handleModalAction("delete", item)
                                }
                                sx={{
                                  color: "#F57171",
                                  cursor: "pointer",
                                  fontSize: 18,
                                }}
                              />
                            </CustomTooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <p className="textwhite f-14 mb-0">No Admins found.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Col>

        <Col md={12}>
          <p className="tablebottomText">
            {" "}
            <img src={Flag} height="18px" alt="" className="me-1" /> Russian
            user data is processed and stored in accordance with the legal
            requirements of the Russian Federation (Federal Law No. 152-FZ).
          </p>
        </Col>

        <Col md={12} className="text-end">
          <div className="d-flex justify-content-end">
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </Col>
      </Row>

      {/* Delete Modal ----------------------- */}

      <CustomModal
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          setModalState({ name: "", user: null });
        }}
        title={
          modalState.name === "delete" ? "Confirm Deletion" : "User Details"
        }
        showCloseButton={true}
        footerButtons={
          <>
            {modalState.name === "delete" && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <Button
                  variant="secondary"
                  style={{ padding: "5px 30px" }}
                  onClick={() => {
                    setModalShow(false);
                    setModalState({ name: "", user: null });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  style={{ padding: "5px 30px" }}
                  onClick={confirmDelete}
                >
                  Delete
                </Button>
              </div>
            )}
          </>
        }
      >
        {modalState?.name === "delete" && (
          <p className="my-4 text-center textwhite" style={{ fontSize: 18 }}>
            Are you sure you want to delete{" "}
            <strong>{modalState?.user?.fullName}</strong>?
          </p>
        )}

        {modalState?.name === "view" && modalState.user && (
          <Row>
            <Col md={6}>
              <div className="text-start">
                <p className="input-label mb-0 input-label">
                  <strong>Name:</strong>
                </p>
                <p className="input-label">
                  {modalState?.user?.fullName || "-----"}
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="text-start">
                <p className="input-label mb-0 input-label">
                  <strong>Email:</strong>
                </p>
                <p className="input-label">
                  {modalState?.user?.email || "-----"}
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="text-start">
                <p className="input-label mb-0 input-label">
                  <strong>Phone:</strong>
                </p>
                <p className="input-label">
                  {modalState?.user?.mobile || "-----"}
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="text-start">
                <p className="input-label  mb-0 input-label">
                  <strong>Country:</strong>{" "}
                </p>
                <p className="input-label">
                  {modalState?.user?.country?.name || "----"}
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="text-start">
                <p className="mb-0 input-label mb-0">
                  <strong>Status:</strong>{" "}
                </p>
                <p className="mb-0 input-label">
                  {modalState?.user?.status || "-----"}
                </p>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <p className="mb-1 input-label  mb-0">
                <strong>User Profile</strong>{" "}
              </p>
              {modalState.user?.profilePic && (
                <img

                  src={
                    modalState?.user?.profilePic?.startsWith("http")
                      ? modalState.user.profilePic
                      : `${process.env.REACT_APP_API_ORIGIN}/${modalState?.user?.profilePic?.replace(/^\/+/, "")}`
                  }

                  alt=""
                  style={{ width: 80, height: 80, borderRadius: "10%" }}
                />
              )}
            </Col>
          </Row>
        )}
      </CustomModal>

      {/* Filter Modal -------------------------------- */}

      <CustomModal
        show={filterModal}
        onHide={() => {
          setFilterModal(false);
        }}
        title="List of Users"
        subtitle="Filter"
        showCloseButton={true}
        footerButtons={
          <>
            <div
              style={{
                display: "flex",
                marginTop: 10,
                justifyContent: "flex-start",
                gap: "10px",
                width: "100%",
              }}
            >
              <Button
                variant="secondary"
                style={{
                  backgroundColor: "rgba(75, 128, 235, 1)",
                  borderColor: "rgba(75, 128, 235, 1)",
                  padding: "8px 30px",
                  borderRadius: 10,
                }}
                onClick={() => {
                  setFilterModal(false);
                  fetchUsers();
                  // setCurrentPage(1); // reset pagination
                }}
              >
                Apply Filters
              </Button>
            </div>
          </>
        }
      >
        <Row className="mt-3">
          <Col md={6} className="mb-3">
            <p className="mb-1 input-label f-14">Select ID No.</p>
            <CustomSelect
              options={filterOptions.memberId}
              value={filters.memberId}
              onChange={(option) =>
                setFilters((prev) => ({ ...prev, memberId: option.value }))
              }
              placeholder="Select"
            />
          </Col>
          <Col md={6} className="mb-3">
            <p className="mb-1 input-label f-14">Select Email</p>
            <CustomSelect
              options={filterOptions.email}
              value={filters.email}
              onChange={(option) =>
                setFilters((prev) => ({ ...prev, email: option.value }))
              }
              placeholder="Select"
            />
          </Col>
          <Col md={6}>
            <p className="mb-1 input-label f-14">Select Country</p>
            <CustomSelect
              options={filterOptions.country}
              value={filters.country}
              onChange={(option) =>
                setFilters((prev) => ({ ...prev, country: option.value }))
              }
              placeholder="Select"
            />
          </Col>
          <Col md={6}>
            <p className="mb-1 input-label f-14">Select Status</p>
            <CustomSelect
              options={filterOptions.status}
              value={filters.status}
              onChange={(option) =>
                setFilters((prev) => ({ ...prev, status: option.value }))
              }
              placeholder="Select"
            />
          </Col>
        </Row>
      </CustomModal>
    </>
  );
}
